// ─── Feuille : créer / éditer une LIGNE PRÉVUE ────────────────────
// La prévision : montant estimé, signe, enveloppe, catégorie, date de début,
// répétition (indéfinie ou jusqu'à un mois de fin). Cf. CONCEPTION-FINANCE.md.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, ChoixPuces, SelecteurListe, stylesForm, useUI, useT } from '@mop/core';
import { cleCategorie, emojiBudget } from './data/categories';
import { categoriesActives, sousCategoriesDe } from './services/categoriesPerso';
import { FREQUENCES } from './services/lignes';
import { decalerMois, moisEntre } from './services/occurrences';
import { formatEuro } from './services/calculs';
import SectionCredit from './SectionCredit';

const moisCourant = () => new Date().toISOString().slice(0, 7);
const VIDE = {
  libelle: '', montant: '', sens: 'depense', budgetId: null, categorie: 'autre',
  debut: '', fin: '', frequence: 'mensuelle', jour: '1', inflationTaux: '', sousCategorie: '',
};
// Une ligne à CRÉDIT (17/08) n'a ni fréquence ni inflation propres (cf.
// `services/occurrences.js`) — ces champs restent dans `f` mais sont
// masqués/ignorés dès que `credit.actif`.
const VIDE_CREDIT = {
  actif: false, mode: 'mensualite', capital: '', dateCapital: '', taux: '',
  // `dureeUnite.moisExact` FAIT FOI (ce que `services/credit.js` lit et ce
  // qu'on enregistre) ; `unite` ne choisit que l'AFFICHAGE dans `SectionCredit`
  // — changer d'unité CONVERTIT (24 mois ↔ 2 ans), contrairement au
  // `SelecteurDuree` générique, dont ce n'est justement pas le rôle ici.
  dureeUnite: { moisExact: 0, unite: 'mois' },
  differe: '', assurance: '', rembCapitalDeblocage: false,
  paliers: [], deblocages: [],
};

// « Mai 2026 » se relit ; « 2026-05 » se déchiffre.
const libelleMois = (mk, t) => {
  const [y, m] = String(mk || '').split('-').map(Number);
  const noms = t('commun.moisLongs');
  return `${(Array.isArray(noms) ? noms[m - 1] : '') || mk || ''} ${y || ''}`.trim();
};

export default function FeuilleLigne({
  visible, initial, budgets = [], onEnregistrer, onFermer,
  // ⚠️ Le sélecteur de mois est une <Feuille>, et on est DÉJÀ dans une
  // Feuille : deux <Modal> imbriquées figent l'écran. C'est donc l'ÉCRAN qui
  // le rend, en sœur — même mécanisme que `onDemanderDate` de FeuilleOperation.
  onDemanderMois,
  // Catégories actives de l'espace (défauts non masqués + personnalisées).
  configCategories,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [f, setF] = useState(VIDE);
  const [avanceOuvert, setAvanceOuvert] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const l = initial || {};
    const c = l.credit || {};
    setF({
      ...VIDE, ...l,
      montant: l.montant != null ? String(l.montant) : '',
      debut: l.debut || moisCourant(),
      fin: l.fin || '',
      jour: String(l.jour || 1),
      inflationTaux: l.inflationTaux ? String(l.inflationTaux) : '',
      credit: {
        ...VIDE_CREDIT, ...c, actif: !!c.actif,
        capital: c.capital ? String(c.capital) : '',
        taux: c.taux ? String(c.taux) : '',
        // La base stocke toujours des MOIS ; rouvrir une ligne l'affiche donc
        // en mois — rien de perdu, juste redemandé en années si on préfère.
        dureeUnite: { moisExact: Number(c.duree) || 0, unite: 'mois' },
        differe: c.differe ? String(c.differe) : '',
        assurance: c.assurance ? String(c.assurance) : '',
        paliers: (c.paliers || []).map(p => ({ mensualite: String(p.mensualite ?? ''), duree: String(p.duree ?? '') })),
        deblocages: (c.deblocages || []).map(d => ({ date: (d.date || '').slice(0, 7), pct: String(d.pct ?? '') })),
      },
    });
    setAvanceOuvert(false);
  }, [visible, initial]);

  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const setCredit = (patch) => setF(x => ({ ...x, credit: { ...x.credit, ...patch } }));
  const moisOk = (v) => /^\d{4}-\d{2}$/.test(v);
  const montantNum = parseFloat(String(f.montant).replace(',', '.')) || 0;
  const credit = f.credit || VIDE_CREDIT;
  const enCredit = !!credit.actif;
  const creditParams = enCredit && credit.mode === 'params';
  // Cohérences bloquantes : sans elles la ligne n'engendrerait aucune occurrence
  // exploitable (ou une infinité à l'envers). Mode « params » : c'est le CAPITAL
  // et la DURÉE qui portent l'exigence, le montant est dérivé, pas saisi.
  const finIncoherente = !!f.fin && (!moisOk(f.fin) || f.fin < f.debut);
  const creditOk = !enCredit || (creditParams
    ? (parseFloat(String(credit.capital).replace(',', '.')) || 0) > 0 && (credit.dureeUnite?.moisExact || 0) > 0
    : montantNum > 0);
  const valide = !!(f.libelle || '').trim() && (enCredit ? creditOk : montantNum > 0)
    && moisOk(f.debut) && !finIncoherente;
  // Étalement d'une ponctuelle : nombre de mois ↔ date de fin, même donnée.
  const nbMoisEtale = (moisOk(f.debut) && moisOk(f.fin) && !finIncoherente)
    ? moisEntre(f.debut, f.fin).length : 1;
  const changerNbMois = (d) => {
    const n = Math.max(1, nbMoisEtale + d);
    set('fin', n === 1 ? '' : decalerMois(f.debut, n - 1));
  };

  return (
    <Feuille visible={visible} titre={initial?.id ? t('finances.modifierLigne') : t('finances.nouvelleLigne')}
      emoji={f.sens === 'revenu' ? '💶' : '📉'} hauteur={0.85} onFermer={onFermer}
      action={{ icone: initial?.id ? '💾' : '＋', label: t('commun.enregistrer'), disabled: !valide,
        onPress: () => onEnregistrer({ ...f, montant: montantNum, jour: Number(f.jour) || 1,
          inflationTaux: enCredit ? 0 : (parseFloat(String(f.inflationTaux).replace(',', '.')) || 0),
          // Un crédit n'a ni FIN (son échéancier la porte) ni FRÉQUENCE
          // propre (`services/lignes.js` force 'mensuelle' de toute façon).
          fin: enCredit ? null : (f.fin || null), sousCategorie: f.sousCategorie || null,
          // `dureeUnite` ne sert qu'à CE formulaire (mois/année, affichage) —
          // seul `moisExact` compte, `services/credit.js` ne connaît que ça.
          credit: enCredit ? (() => {
            const { dureeUnite, ...c } = credit;
            return { ...c, duree: String(dureeUnite?.moisExact || 0) };
          })() : null }) }}>

      <ChoixPuces
        options={[{ id: 'depense', label: `📉 ${t('budget.depense')}` }, { id: 'revenu', label: `💶 ${t('budget.revenus')}` }]}
        value={f.sens} onChange={v => set('sens', v)} />

      <Text style={s.lbl}>{t('budget.libelle')}</Text>
      <TextInput style={s.input} value={f.libelle} onChangeText={v => set('libelle', v)}
        placeholder={t('finances.libellePlaceholder')} placeholderTextColor={UI.faint} />

      {/* CRÉDIT (17/08) : une ligne à amortissement n'a ni fréquence propre
          ni inflation — son échéancier EST sa courbe (services/credit.js).
          La bascule remplace ces deux champs par la section dédiée. */}
      <Text style={s.lbl}>{t('finances.creditBascule')}</Text>
      <ChoixPuces
        options={[{ id: 'classique', label: t('finances.creditClassique') }, { id: 'credit', label: `🏦 ${t('finances.credit')}` }]}
        value={enCredit ? 'credit' : 'classique'}
        onChange={v => setCredit({ actif: v === 'credit' })} />

      {/* Mode « params » : le montant n'est pas saisi ici, il est DÉRIVÉ de
          l'échéancier (cf. services/lignes.js) — l'afficher inviterait à le
          remplir pour rien. */}
      {!creditParams && (
        <>
          <Text style={s.lbl}>{t(enCredit ? 'finances.creditMensualiteConnue' : 'finances.montantPrevu')}</Text>
          <TextInput style={[s.input, s.montant]} value={f.montant}
            onChangeText={v => set('montant', v.replace(/[^0-9.,]/g, ''))}
            keyboardType="decimal-pad" placeholder="0,00" placeholderTextColor={UI.faint} />
        </>
      )}

      {enCredit && (
        <SectionCredit valeur={credit} onChange={setCredit} onDemanderMois={onDemanderMois}
          debutLigne={f.debut} montantLigne={montantNum} jourLigne={Number(f.jour) || 1}
          avanceOuvert={avanceOuvert} onToggleAvance={() => setAvanceOuvert(v => !v)} />
      )}
      {/* Sans ce message, le ＋ reste grisé sans qu'on sache pourquoi — vécu
          par Cédric le 17/08 : Durée à 0, bouton mort, aucune explication. */}
      {enCredit && !creditOk && (
        <Text style={s.erreurTxt}>
          {t(creditParams ? 'finances.creditErreurParams' : 'finances.creditErreurMensualite')}
        </Text>
      )}

      <Text style={s.lbl}>{t('famille.t.finances')}</Text>
      {budgets.length === 0 ? (
        <Text style={s.aide}>{t('budget.aucunBudget')}</Text>
      ) : (
        <SelecteurListe titre={t('famille.t.finances')} emoji="💰"
          options={budgets.map(b => ({ id: b.id, label: b.nom, emoji: emojiBudget(b) }))}
          valeur={f.budgetId} onChange={v => set('budgetId', v)}
          avecVide libelleVide={t('budget.sansBudget')} />
      )}

      <Text style={s.lbl}>{t('budget.categorie')}</Text>
      <SelecteurListe titre={t('budget.categorie')} emoji="🏷️"
        options={categoriesActives(configCategories).map(c => ({ id: c.id, label: c.nom || t(cleCategorie(c.id)), emoji: c.emoji }))}
        valeur={f.categorie} onChange={v => setF(x => ({ ...x, categorie: v, sousCategorie: '' }))} />

      {/* SOUS-CATÉGORIE, entièrement optionnelle (Cédric, 15/08) : le
          sélecteur n'apparaît que si la catégorie en a — pas de champ mort
          sur les autres. */}
      {sousCategoriesDe(f.categorie, configCategories).length > 0 && (
        <>
          <Text style={s.lbl}>{t('finances.sousCategorie')}</Text>
          <SelecteurListe titre={t('finances.sousCatOptionnelle')} emoji="🏷️"
            avecVide libelleVide={t('commun.aucune')}
            options={sousCategoriesDe(f.categorie, configCategories).map(x => ({ id: x, label: x }))}
            valeur={f.sousCategorie} onChange={v => set('sousCategorie', v || '')} />
        </>
      )}

      {!enCredit && (
        <>
          <Text style={s.lbl}>{t('budget.frequence')}</Text>
          <SelecteurListe titre={t('budget.frequence')} emoji="🔁"
            options={FREQUENCES.map(x => ({ id: x, label: t(`budget.freq_${x}`) }))}
            valeur={f.frequence} onChange={v => set('frequence', v)} />
        </>
      )}

      <View style={s.row}>
        <View style={{ flex: 1 }}>
          <Text style={s.lbl}>{t('finances.debut')}</Text>
          {/* Bouton et non saisie : « AAAA-MM » tapé à la main se trompe
              d'un chiffre sans rien dire (Cédric, 14/08). Pour un crédit,
              c'est aussi l'ANCRE de repli quand la date de référence de la
              section Crédit est vide. */}
          <TouchableOpacity style={[s.input, s.champBtn, !moisOk(f.debut) && s.erreur]}
            activeOpacity={0.7}
            onPress={() => onDemanderMois?.(f.debut, (mk) => set('debut', mk))}>
            <Text style={[s.champTxt, !f.debut && { color: UI.faint }]}>
              {f.debut ? libelleMois(f.debut, t) : 'AAAA-MM'}
            </Text>
          </TouchableOpacity>
        </View>
        {/* La FIN vaut aussi pour une ponctuelle (Cédric, 16/08) : elle
            l'ÉTALE. Un crédit n'en a PAS besoin : son terme est celui de son
            échéancier (durée, paliers…), pas ce champ. */}
        {!enCredit && (
          <View style={{ flex: 1 }}>
            <Text style={s.lbl}>{t('finances.fin')}</Text>
            <TouchableOpacity style={[s.input, s.champBtn, finIncoherente && s.erreur]}
              activeOpacity={0.7}
              onPress={() => onDemanderMois?.(f.fin, (mk) => set('fin', mk), true)}>
              <Text style={[s.champTxt, !f.fin && { color: UI.faint }]}>
                {f.fin ? libelleMois(f.fin, t)
                  : t(f.frequence === 'ponctuelle' ? 'finances.unSeulMois' : 'finances.sansFin')}
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
      {!enCredit && finIncoherente && <Text style={s.erreurTxt}>{t('finances.finAvantDebut')}</Text>}
      {!enCredit && f.frequence !== 'ponctuelle' && !f.fin && <Text style={s.aide}>{t('finances.sansFinAide')}</Text>}

      {/* « … OU un nombre de mois » : le compteur et la date de fin sont la
          même information, liés dans les deux sens. */}
      {!enCredit && f.frequence === 'ponctuelle' && moisOk(f.debut) && (
        <>
          <View style={s.rangeeEtale}>
            <Text style={s.etaleLbl}>{t('finances.etalerSur')}</Text>
            <TouchableOpacity style={s.btnPas} activeOpacity={0.7} disabled={nbMoisEtale <= 1}
              onPress={() => changerNbMois(-1)}>
              <Text style={[s.btnPasTxt, nbMoisEtale <= 1 && { opacity: 0.3 }]}>−</Text>
            </TouchableOpacity>
            <Text style={s.etaleN}>{nbMoisEtale}</Text>
            <TouchableOpacity style={s.btnPas} activeOpacity={0.7} onPress={() => changerNbMois(1)}>
              <Text style={s.btnPasTxt}>＋</Text>
            </TouchableOpacity>
            <Text style={s.etaleLbl}>{t('finances.moisMot')}</Text>
          </View>
          {nbMoisEtale > 1 && montantNum > 0 && (
            <Text style={s.aide}>
              {t('finances.etaleAide', { m: formatEuro(Math.round((montantNum / nbMoisEtale) * 100) / 100) })}
            </Text>
          )}
        </>
      )}

      {!enCredit && f.frequence !== 'ponctuelle' && (
        <>
          <Text style={s.lbl}>{t('finances.inflation')}</Text>
          <TextInput style={s.input} value={f.inflationTaux}
            onChangeText={v => set('inflationTaux', v.replace(/[^0-9.,]/g, ''))}
            keyboardType="decimal-pad" placeholder="0" placeholderTextColor={UI.faint} />
          <Text style={s.aide}>{t('finances.inflationAide')}</Text>
        </>
      )}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  // Un bouton doit centrer son texte verticalement là où un TextInput
  // le faisait tout seul.
  champBtn: { justifyContent: 'center' },
  champTxt: { fontSize: 15, fontWeight: '700', color: UI.ink },
  row: { flexDirection: 'row', gap: 8 },
  montant: { fontSize: 20, fontWeight: '800', textAlign: 'right' },
  aide: { fontSize: 12, color: UI.faint, lineHeight: 17, marginTop: 5 },
  erreur: { borderColor: UI.danger || '#c0392b', borderWidth: 1 },
  erreurTxt: { fontSize: 12, color: UI.danger || '#c0392b', marginTop: 5 },
  rangeeEtale: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 10 },
  etaleLbl: { fontSize: 13, color: UI.muted, fontWeight: '700' },
  etaleN: { fontSize: 16, fontWeight: '800', color: UI.ink, minWidth: 26, textAlign: 'center' },
  btnPas: { width: 36, height: 36, borderRadius: 10, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: UI.accent },
  btnPasTxt: { fontSize: 17, color: UI.accent, fontWeight: '700' },
});
